// ══════════════════════════════════════════════════════════════════════════════
// SELDOR — LLM Prompt Templates
// Single source of truth for prompts sent to Claude API.
// Design: system prompt sets role + constraints; user prompt is data payload.
// Output format matches Mock AI exactly, so swapping engines is zero-friction.
// ══════════════════════════════════════════════════════════════════════════════

export const SYSTEM_PROMPT = `You are Seldor Oracle, a senior F&B business analyst specializing in Saudi Arabian cafes and restaurants.

Your role:
- Analyze operational, financial, and marketing data
- Generate executive-level insights with specific, quantified recommendations
- Always provide reasoning ("why?") grounded in the actual data you're given
- Always provide actionable steps ("how?") with concrete timelines
- Write insights in BOTH Arabic (primary) and English

Constraints:
- Every insight MUST include expected_impact in SAR (zero if pure advisory)
- Severity levels: "critical" (active loss > SAR 1000/mo), "warning" (opportunity > SAR 500/mo or risk), "info" (advisory)
- Categories you MUST cover when data warrants:
  * financial — cost leaks, margin erosion, cash flow
  * operational / staffing / fraud — labor efficiency, process issues
  * pricing — selling price optimization
  * opportunity — supplier alternatives, negotiation leverage
  * marketing — growth levers, customer patterns, promotions
  * inventory — stock issues
- Use Saudi market context (SAR currency, local business culture, Arabic names)
- NEVER fabricate numbers — if data is missing, say so or skip that angle
- Max 8 insights total, ranked by severity × expected impact

Output format: Return ONLY a valid JSON array. No markdown, no prose before/after.
Each object must have exactly these fields:
{
  "insight_type": "financial|operational|pricing|staffing|fraud|opportunity|marketing|inventory|customer_pattern",
  "severity": "critical|warning|info",
  "title_ar": "string (Arabic title, < 80 chars)",
  "title_en": "string (English title, < 80 chars)",
  "description_ar": "string (Arabic 1-2 sentence summary)",
  "description_en": "string",
  "why_ar": "string (detailed Arabic analysis, 2-4 paragraphs, cite specific data)",
  "why_en": "string",
  "how_ar": "string (Arabic action plan, numbered steps, concrete timeline)",
  "how_en": "string",
  "expected_impact": number (SAR per month)
}`;

export const USER_PROMPT_TEMPLATE = (contextSummary) => `Analyze the following business data for a Saudi cafe and generate 4-8 strategic insights.

DATA CONTEXT:
${JSON.stringify(contextSummary, null, 2)}

REQUIREMENTS:
1. Cover at minimum: one financial, one operational, one marketing insight (if data supports)
2. Prioritize by actual monetary impact first, then severity
3. Every "why_ar" / "why_en" must reference specific numbers from the data above
4. Every "how_ar" / "how_en" must be implementable within 30 days
5. Return strictly the JSON array, nothing else`;

/**
 * Build the context summary that gets injected into the prompt.
 * Mirrors what MockAIOracle.analyzeContext produces.
 */
export function buildPromptContext(data) {
  const {
    sales = [], employees = [], gaps = [], ingredients = [],
    marketPrices = [], products = [], branches = [], cancellations = [], tenant
  } = data || {};

  const totalRev30d = sales.slice(0, 30).reduce((s, d) => s + parseFloat(d.total_revenue || 0), 0);
  const recent7 = sales.slice(0, 7);
  const prev7 = sales.slice(7, 14);
  const recentAvg = recent7.length ? recent7.reduce((s, d) => s + parseFloat(d.total_revenue || 0), 0) / recent7.length : 0;
  const prevAvg = prev7.length ? prev7.reduce((s, d) => s + parseFloat(d.total_revenue || 0), 0) / prev7.length : 0;

  // Day-of-week patterns
  const byDayOfWeek = [0, 0, 0, 0, 0, 0, 0];
  const countByDay = [0, 0, 0, 0, 0, 0, 0];
  sales.slice(0, 28).forEach(s => {
    if (!s.sale_date) return;
    const day = new Date(s.sale_date).getDay();
    byDayOfWeek[day] += parseFloat(s.total_revenue || 0);
    countByDay[day]++;
  });
  const avgByDay = byDayOfWeek.map((total, i) => countByDay[i] ? Math.round(total / countByDay[i]) : 0);

  return {
    business_overview: {
      business_name_ar: tenant?.name_ar,
      business_name_en: tenant?.name_en,
      subscription_tier: tenant?.subscription_tier,
      branch_count: branches.length,
      city: tenant?.city || 'Riyadh',
      product_count: products.length,
      employee_count: employees.length
    },
    financial_snapshot: {
      revenue_last_30_days_sar: Math.round(totalRev30d),
      avg_daily_revenue_sar: Math.round(totalRev30d / Math.min(30, sales.length || 1)),
      recent_week_vs_previous_week_change_pct:
        prevAvg > 0 ? +(((recentAvg - prevAvg) / prevAvg) * 100).toFixed(1) : null,
      avg_gross_margin_pct: products.length
        ? +(products.reduce((s, p) => s + parseFloat(p.profit_margin), 0) / products.length).toFixed(1)
        : null,
      active_financial_gaps: gaps.length,
      total_estimated_monthly_loss_sar: Math.round(
        gaps.reduce((s, g) => s + parseFloat(g.estimated_monetary_loss || 0), 0)
      )
    },
    day_of_week_revenue_sar: {
      Sunday: avgByDay[0], Monday: avgByDay[1], Tuesday: avgByDay[2],
      Wednesday: avgByDay[3], Thursday: avgByDay[4], Friday: avgByDay[5], Saturday: avgByDay[6]
    },
    time_of_day_split_last_14_days: {
      morning_sar: Math.round(sales.slice(0, 14).reduce((s, d) => s + parseFloat(d.morning_revenue || 0), 0)),
      afternoon_sar: Math.round(sales.slice(0, 14).reduce((s, d) => s + parseFloat(d.afternoon_revenue || 0), 0)),
      evening_sar: Math.round(sales.slice(0, 14).reduce((s, d) => s + parseFloat(d.evening_revenue || 0), 0))
    },
    flagged_employees: employees
      .filter(e => e.is_flagged || parseFloat(e.honesty_score) < 70)
      .map(e => ({
        name_ar: e.name_ar,
        name_en: e.name_en,
        role: e.role_en,
        honesty_score: parseFloat(e.honesty_score),
        total_cancellations: e.total_cancellations,
        total_transactions: e.total_transactions,
        cancellation_rate_pct: e.total_transactions
          ? +((e.total_cancellations / e.total_transactions) * 100).toFixed(1) : 0
      })),
    critical_financial_gaps: gaps
      .filter(g => g.severity === 'critical' || g.severity === 'high')
      .map(g => ({
        type: g.gap_type,
        description_ar: g.description_ar,
        description_en: g.description_en,
        expected: parseFloat(g.expected_value),
        actual: parseFloat(g.actual_value),
        estimated_monthly_loss_sar: parseFloat(g.estimated_monetary_loss)
      })),
    product_margins: products.map(p => ({
      name_ar: p.name_ar,
      name_en: p.name_en,
      category: p.category_en,
      selling_price: parseFloat(p.selling_price),
      cost_price: parseFloat(p.cost_price),
      margin_pct: parseFloat(p.profit_margin)
    })).sort((a, b) => a.margin_pct - b.margin_pct).slice(0, 10),
    supplier_price_deviations: ingredients
      .map(ing => {
        const market = marketPrices.find(m => m.material_name_ar === ing.name_ar);
        if (!market) return null;
        const you = parseFloat(ing.purchase_price);
        const ref = parseFloat(market.market_price);
        const diffPct = ((you - ref) / ref) * 100;
        if (Math.abs(diffPct) < 5) return null;
        const monthlyConsumption = parseFloat(ing.actual_consumption_monthly || 20);
        return {
          material_ar: ing.name_ar,
          material_en: ing.name_en,
          unit: ing.unit,
          your_price: you,
          market_price: ref,
          deviation_pct: +diffPct.toFixed(1),
          monthly_consumption: monthlyConsumption,
          monthly_overspend_sar: +((you - ref) * monthlyConsumption).toFixed(2)
        };
      })
      .filter(Boolean)
      .sort((a, b) => b.monthly_overspend_sar - a.monthly_overspend_sar)
      .slice(0, 8),
    suspicious_voids_count: cancellations.filter(c =>
      c.suspicion_level === 'critical' || c.suspicion_level === 'high'
    ).length
  };
}

/**
 * Complete prompt builder — returns messages array ready for API.
 */
export function buildMessages(data) {
  const ctx = buildPromptContext(data);
  return {
    system: SYSTEM_PROMPT,
    messages: [
      { role: 'user', content: USER_PROMPT_TEMPLATE(ctx) }
    ]
  };
}
